import { computed, Ref } from "@vue/composition-api";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { blockPurchaseIfAgeNotVerified } from "@/lib/ageVerificationPurchaseGuard";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import PaymentManager from "@/payments";
import { PurchaseDetails, PurchaseInfo } from "@/payments/types";
import config from "@/config";
import { EVENTS, ERRORS } from "@/const";

export default function usePurchaseSubscription(
  subscriptionLevel: Ref<SubscriptionLevel | null>,
  purchaseDetails: Ref<PurchaseDetails>
) {
  const { authUser } = useAuthUser();

  const subscribeByCard = async (): Promise<boolean> => {
    if (blockPurchaseIfAgeNotVerified(authUser.value)) {
      return false;
    }
    const response = await PaymentManager.subscribe(purchaseDetails.value);
    return response.status;
  };

  const subscribeWithBalance = async (): Promise<Subscription | undefined> => {
    if (blockPurchaseIfAgeNotVerified(authUser.value)) {
      return;
    }
    if (!subscriptionLevel.value) return;
    let response;
    try {
      providers.SubscriptionProvider.setReceiveErrors(false);
      response = await providers.SubscriptionProvider.purchase({ levelId: subscriptionLevel.value.id });
    } catch (e) {
      handleRequestError(e, [
        {
          comparator: e => e.details?.messageTemplate === ERRORS.SPENDING_BALANCE,
          onSuccessCB: () => emitter.emit(EVENTS.BALANCE_REFILL_SHOW),
        },
      ]);
    }
    return response;
  };

  const purchaseInfoDetails = computed<PurchaseInfo>(() => {
    return {
      amount: purchaseDetails.value.price ?? "",
      paymentMethod: config.featureSubscribeFromBalance ? "balance" : "card",
      purchaseType: "subscription",
    };
  });

  return {
    performPurchaseSubscription: config.featureSubscribeFromBalance ? subscribeWithBalance : subscribeByCard,
    purchaseInfoDetails,
  };
}
